import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX } from 'react-icons/fi';
import { adminTheme } from '../../theme-admin';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  z-index: ${adminTheme.zIndex.modal};
  animation: ${fadeIn} 0.2s ease-out;
  padding: ${adminTheme.spacing.lg};
`;

const Card = styled.div`
  background: ${adminTheme.colors.background.primary};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  width: min(480px, 100%);
  box-shadow: ${adminTheme.shadows['2xl']};
  animation: ${scaleIn} 0.25s ease-out;
  border: 1px solid ${adminTheme.colors.gray[200]};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${adminTheme.spacing.lg};
  right: ${adminTheme.spacing.lg};
  width: 32px;
  height: 32px;
  border-radius: ${adminTheme.borderRadius.lg};
  border: none;
  background: transparent;
  color: ${adminTheme.colors.gray[400]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    color: ${adminTheme.colors.gray[600]};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${adminTheme.gradients.danger};
  border-radius: ${adminTheme.borderRadius.xl};
  font-size: ${adminTheme.fontSizes['3xl']};
  margin-bottom: ${adminTheme.spacing.lg};
  box-shadow: ${adminTheme.shadows.md};
`;

const Title = styled.h3`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes.xl};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0 0 ${adminTheme.spacing.sm};
`;

const Desc = styled.p`
  font-size: ${adminTheme.fontSizes.base};
  color: ${adminTheme.colors.gray[600]};
  margin: 0 0 ${adminTheme.spacing.xl};
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${adminTheme.spacing.md};
`;

const Button = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  border-radius: ${adminTheme.borderRadius.lg};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    border-color: ${adminTheme.colors.gray[400]};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Danger = styled(Button)`
  background: ${adminTheme.gradients.danger};
  border: none;
  color: white;
  box-shadow: ${adminTheme.shadows.md};

  &:hover {
    filter: brightness(1.1);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

export default function ConfirmDialog({ 
  open, 
  title = 'Are you sure?', 
  description, 
  onCancel, 
  onConfirm,
  icon = '⚠️'
}) {
  if (!open) return null;
  
  return (
    <Backdrop>
      <Card onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
        <CloseButton onClick={onCancel} aria-label="Close dialog">
          <FiX />
        </CloseButton>
        <IconWrapper>{icon}</IconWrapper>
        <Title>{title}</Title>
        {description && <Desc>{description}</Desc>}
        <Row>
          <Button onClick={onCancel}>Cancel</Button>
          <Danger onClick={onConfirm}>Confirm</Danger>
        </Row>
      </Card>
    </Backdrop>
  );
}
